<template>
  <div class="step-message">
    <!-- メッセージノードリスト -->
    <div v-if="messages.length > 0" class="message-nodes-list">
      <div v-for="(message, index) in messages" :key="`message-${index}-${message.type}`" class="message-node-item">
        <!-- タイプセレクト -->
        <div class="message-node-type">
          <el-select
            :value="message.type"
            @change="handleTypeChange(index, $event)"
            class="message-type-select"
            style="width: 200px"
            :disabled="campaignRunning"
          >
            <el-option :label="'テキスト'" value="text" :disabled="!canChangeToType(index, 'text')" />
            <el-option :label="'メディア'" value="media" :disabled="!canChangeToType(index, 'media')" />
            <el-option :label="'アンケート'" value="select" :disabled="!canChangeToType(index, 'select')" />
            <el-option :label="'カード'" value="card" :disabled="!canChangeToType(index, 'card')" />
          </el-select>
        </div>

        <!-- ノードコンテンツ（タイプ別フォーム）-->
        <div class="message-node-content" :key="message.type">
          <!-- テキストノード -->
          <div v-if="message.type === 'text'" class="text-node-form">
            <el-input
              :value="message.text"
              @input="handleTextChange(index, $event)"
              type="textarea"
              :rows="6"
              :placeholder="'テキスト'"
              :class="{ 'is-error': getTextNodeError(index) }"
            />
            <div v-if="getTextNodeError(index)" class="input-invalid-text">
              {{ getTextNodeError(index) }}
            </div>
          </div>

          <!-- メディアノード -->
          <div v-if="message.type === 'media'" class="media-node-form" v-loading="store.uploadLoading">
            <!-- プレビュー表示 -->
            <div v-if="message.url">
              <div class="media-preview">
                <i class="mdi mdi-close" @click.stop="handleMediaDelete(index)"></i>
                <div class="media-name">{{ getMediaFileName(message) }}</div>
                <!-- 画像プレビュー -->
                <img v-if="isImageFile(message)" :src="message.url" alt="プレビュー" class="direct-media" />
                <!-- 動画プレビュー -->
                <video v-else-if="!isImageFile(message)" :src="message.url" controls class="direct-media"></video>
              </div>
              <div v-if="getMediaNodeError(index)" class="input-invalid-text media-error-inline">
                {{ getMediaNodeError(index) }}
              </div>
            </div>

            <!-- ファイル選択ボタン（プレビューがない場合のみ表示） -->
            <div v-else class="media-upload-container">
              <el-button class="upload-btn" @click="handleMediaSelectClick(index)">
                <i class="mdi mdi-image-plus" />
                {{ '画像・動画を追加' }}
              </el-button>
              <p class="upload-hint">
                {{ 'アップロード可能な最大サイズ: 画像8MB 動画25MB' }}
              </p>
              <!-- 非表示のファイル入力 -->
              <input
                :id="`fileInput-${index}`"
                type="file"
                accept="image/png,image/jpeg,video/mp4"
                style="display: none"
                @change="handleMediaFileChange(index, $event)"
              />
            </div>
            <div v-if="getMediaNodeError(index)" class="input-invalid-text media-error-inline">
              {{ getMediaNodeError(index) }}
            </div>
          </div>

          <!-- アンケートノード -->
          <div v-if="message.type === 'select'" class="select-node-form">
            <!-- 質問テキストエリア -->
            <div class="question-field">
              <label class="field-label">{{ '質問' }}</label>
              <el-input
                :value="message.question"
                @input="handleQuestionChange(index, $event)"
                type="textarea"
                :rows="4"
                :placeholder="'質問を入力してください'"
                :class="{ 'is-error': getQuestionError(index) }"
              />
              <div v-if="getQuestionError(index)" class="input-invalid-text">
                {{ getQuestionError(index) }}
              </div>
            </div>

            <!-- 回答セクション -->
            <div class="options-section">
              <!-- ラベル -->
              <div class="options-label">
                <span class="options-count-label">
                  {{ '回答' }}
                  <HelpTooltip placement="top">
                    {{ '最大13個まで選択肢を追加できます' }}
                  </HelpTooltip>
                </span>
              </div>

              <!-- 選択肢入力（Tagsコンポーネント使用） -->
              <div class="options-tags-wrapper">
                <Tags
                  :value="getOptionTexts(index)"
                  :editable="!campaignRunning"
                  :max-items="13"
                  :max-length="260"
                  type="keyword"
                  @input="handleOptionsChange(index, $event)"
                />
              </div>

              <!-- バリデーションエラー表示（選択肢のみ） -->
              <div v-if="hasSelectOptionsErrors(index)" class="select-node-errors">
                <div v-for="(option, optionIndex) in message.options" :key="`error-${optionIndex}`">
                  <div v-if="getOptionError(index, optionIndex)" class="input-invalid-text">
                    {{ '選択肢' }} {{ optionIndex + 1 }}: {{ getOptionError(index, optionIndex) }}
                  </div>
                </div>
                <div v-if="getOptionsCountError(index)" class="input-invalid-text">
                  {{ getOptionsCountError(index) }}
                </div>
              </div>
            </div>
          </div>

          <!-- カードノード -->
          <div v-if="message.type === 'card'" class="card-node-form" v-loading="store.uploadLoading">
            <!-- 画像フィールド -->
            <div class="form-group">
              <label class="form-label">
                {{ '画像' }}
                <span class="required-badge">{{ '必須' }}</span>
              </label>

              <!-- プレビュー表示 -->
              <div v-if="message.url" class="media-preview card-preview">
                <i class="mdi mdi-close" @click.stop="handleCardMediaDelete(index)"></i>
                <div class="media-name">{{ getMediaFileName(message) }}</div>
                <img :src="message.url" alt="プレビュー" class="direct-media" />
                <div v-if="getCardMediaError(index)" class="input-invalid-text media-error-inline">
                  {{ getCardMediaError(index) }}
                </div>
              </div>

              <!-- ファイル選択ボタン（プレビューがない場合のみ表示） -->
              <div v-else class="media-upload-container">
                <el-button class="upload-btn" @click="handleCardMediaSelectClick(index)">
                  <i class="mdi mdi-image-plus" />
                  {{ '画像を追加' }}
                </el-button>
                <p class="upload-hint">
                  {{ 'アップロード可能サイズ: 画像8MB' }}
                </p>
                <!-- 非表示のファイル入力 -->
                <input
                  :id="`cardFileInput-${index}`"
                  type="file"
                  accept="image/png,image/jpeg"
                  style="display: none"
                  @change="handleCardMediaFileChange(index, $event)"
                />
              </div>

              <!-- 画像エラー表示（アップロード欄内に統合） -->
              <div v-if="getCardMediaError(index)" class="input-invalid-text media-error-inline">
                {{ getCardMediaError(index) }}
              </div>
            </div>

            <!-- タイトルフィールド -->
            <div class="form-group">
              <label class="form-label">
                {{ 'タイトル' }}
                <span class="required-badge">{{ '必須' }}</span>
              </label>
              <el-input
                :value="message.title"
                @input="handleCardTitleChange(index, $event)"
                :placeholder="'タイトル'"
                :class="{ 'is-error': getCardTitleError(index) }"
              />
              <div v-if="getCardTitleError(index)" class="input-invalid-text">
                {{ getCardTitleError(index) }}
              </div>
            </div>

            <!-- サブタイトルフィールド -->
            <div class="form-group">
              <label class="form-label">{{ 'サブタイトル' }}</label>
              <el-input
                :value="message.subtitle"
                @input="handleCardSubtitleChange(index, $event)"
                :placeholder="'サブタイトル'"
                :class="{ 'is-error': getCardSubtitleError(index) }"
              />
              <div v-if="getCardSubtitleError(index)" class="input-invalid-text">
                {{ getCardSubtitleError(index) }}
              </div>
            </div>

            <!-- デフォルトURLフィールド -->
            <div class="form-group">
              <label class="form-label">{{ 'デフォルトURL' }}</label>
              <el-input
                :value="message.defaultUrl"
                @input="handleCardDefaultUrlChange(index, $event)"
                :placeholder="'https://www'"
                :class="{ 'is-error': getCardDefaultUrlError(index) }"
              />
              <div v-if="getCardDefaultUrlError(index)" class="input-invalid-text">
                {{ getCardDefaultUrlError(index) }}
              </div>
            </div>

            <!-- ボタンプレビュー（全ボタン） -->
            <div v-if="message.buttons.length > 0" class="card-buttons-preview">
              <el-button
                v-for="(button, buttonIndex) in message.buttons"
                :key="`preview-${buttonIndex}`"
                type="primary"
              >
                {{ getCardButtonPreviewLabel(button.label) }}
              </el-button>
            </div>

            <!-- ボタンセクション -->
            <div class="form-group">
              <label class="form-label">{{ 'ボタン' }}</label>

              <!-- 各ボタンの設定 -->
              <div
                v-for="(button, buttonIndex) in message.buttons"
                :key="`button-${buttonIndex}`"
                class="card-button-item"
              >
                <div class="card-button-header">{{ 'ボタンの設定' }}</div>

                <div class="card-button-fields">
                  <!-- ラベル -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ 'ラベル' }}
                      <span class="required-badge">{{ '必須' }}</span>
                    </label>
                    <el-input
                      :value="button.label"
                      @input="handleCardButtonLabelChange(index, buttonIndex, $event)"
                      :placeholder="'ラベル'"
                      :class="{ 'is-error': getCardButtonLabelError(index, buttonIndex) }"
                    />
                    <div v-if="getCardButtonLabelError(index, buttonIndex)" class="input-invalid-text">
                      {{ getCardButtonLabelError(index, buttonIndex) }}
                    </div>
                  </div>

                  <!-- URL -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ 'URL' }}
                      <span class="required-badge">{{ '必須' }}</span>
                    </label>
                    <el-input
                      :value="button.url"
                      @input="handleCardButtonUrlChange(index, buttonIndex, $event)"
                      :placeholder="'https://www'"
                      :class="{ 'is-error': getCardButtonUrlError(index, buttonIndex) }"
                    />
                    <div v-if="getCardButtonUrlError(index, buttonIndex)" class="input-invalid-text">
                      {{ getCardButtonUrlError(index, buttonIndex) }}
                    </div>
                  </div>
                </div>

                <!-- ボタン削除ボタン -->
                <el-button
                  class="card-button-delete-btn"
                  :disabled="campaignRunning"
                  @click="handleCardButtonDelete(index, buttonIndex)"
                >
                  <i class="mdi mdi-delete-forever" />
                  {{ 'ボタン削除' }}
                </el-button>
              </div>

              <!-- ボタン追加ボタン -->
              <el-button
                class="card-button-add-btn"
                :disabled="campaignRunning || !canAddCardButton(index)"
                @click="handleCardButtonAdd(index)"
              >
                <i class="mdi mdi-plus" />
                {{ 'ボタンを追加' }}
              </el-button>
            </div>
          </div>
        </div>

        <!-- 削除ボタン -->
        <div class="message-node-footer">
          <el-button class="message-delete-btn" :disabled="!canDeleteMessage(index)" @click="handleDelete(index)">
            <i class="mdi mdi-delete-forever" />
            {{ 'メッセージ削除' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- メッセージ追加ボタン -->
    <div class="add-message-section">
      <el-tooltip
        :disabled="!campaignRunning"
        :content="'キャンペーン開催中はメッセージ追加をできません'"
        placement="top"
        :enterable="false"
      >
        <span class="add-message-tooltip">
          <el-button :disabled="isAddMessageDisabled" @click="handleAddMessage" class="add-message-btn">
            <i class="mdi mdi-plus" />
            {{ 'メッセージ追加' }}
          </el-button>
        </span>
      </el-tooltip>
      <p v-if="showLimitMessage" class="limit-message">
        {{ 'メッセージは10個まで追加できます' }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, watch } from 'vue'

import HelpTooltip from '@/client/components/_atoms/HelpTooltip.vue'
import Tags from '@/client/components/_molecules/Tags.vue'
import {
  MessageNode,
  TextNode,
  useInstagramScenarioEditorStore
} from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioEditorStore'
import { TrackingService } from '@/client/services'

const { proxy } = getCurrentInstance() as {
  proxy: {
    $notify: (arg: { title: string; customClass: string; duration: number }) => void
  }
}

// Store
const store = useInstagramScenarioEditorStore()
const { messages, canAddMessage, errors, campaignRunning } = storeToRefs(store)
const isAddMessageDisabled = computed(() => campaignRunning.value || !canAddMessage.value)

const showLimitMessage = computed(() => !campaignRunning.value && !canAddMessage.value)

const getFileExtension = (name: string): string => {
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index).toLowerCase() : ''
}

const isSupportedImageExtension = (name: string): boolean => {
  return ['.jpg', '.jpeg', '.png'].includes(getFileExtension(name))
}

// Getter functions (ラップして公開)
const canDeleteMessage = (index: number): boolean => {
  return store.canDeleteMessage(index)
}

const canChangeToType = (index: number, newType: 'text' | 'media' | 'select' | 'card'): boolean => {
  return store.canChangeToType(index, newType)
}

const getTextNodeError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.text`] || null
}

const getQuestionError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.question`] || null
}

const getOptionError = (index: number, optionIndex: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.options.${optionIndex}`] || null
}

const hasSelectOptionsErrors = (index: number): boolean => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return false
  }

  // 選択肢数のエラーチェック
  if (step2Errors[`messages.${index}.options`]) {
    return true
  }

  // 各選択肢のエラーチェック
  const message = messages.value[index]
  if (message && message.type === 'select') {
    for (let i = 0; i < message.options.length; i++) {
      if (step2Errors[`messages.${index}.options.${i}`]) {
        return true
      }
    }
  }

  return false
}

const getOptionsCountError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.options`] || null
}

const getMediaNodeError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) return null
  return step2Errors[`messages.${index}.media`] || null
}

// メディアノードが画像かどうかを判定
const isImageFile = (message: MessageNode): boolean => {
  if (!message.url) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (message.fileType) {
    return message.fileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = message.url.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || message.url.startsWith('data:image/')
}

// メディアファイル名を取得
const getMediaFileName = (message: MessageNode): string => {
  // fileNameフィールドがあればそれを優先
  if (message.fileName) {
    return message.fileName
  }

  // blob URLの場合は拡張子から生成
  if (message.url && message.url.startsWith('blob:')) {
    const fileType = message.fileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (message.url) {
    const urlWithoutQuery = message.url.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return 'media'
}

const getCardButtonPreviewLabel = (label: string | undefined): string => {
  const normalized = (label || '').trim()
  if (normalized === '') return 'ラベルテキスト'
  return normalized.slice(0, 20)
}

// Methods
const handleAddMessage = () => {
  store.addMessage()
  // メッセージ追加後にバリデーション
  store.validateStep2()
}

const handleDelete = (index: number) => {
  if (!confirm('メッセージを削除しますか？')) {
    return
  }
  store.deleteMessage(index)
  // メッセージ削除後にバリデーション
  store.validateStep2()
}

const handleTypeChange = (index: number, newType: 'text' | 'media' | 'select' | 'card') => {
  // 型変更時にストアへ反映
  store.updateMessageType(index, newType)
  // タイプ変更後にバリデーション
  store.validateStep2()
}

const handleTextChange = (index: number, newText: string) => {
  // テキストノードのテキスト更新
  if (messages.value[index] && messages.value[index].type === 'text') {
    const node = messages.value[index] as TextNode
    node.text = newText

    // リアルタイムバリデーション
    store.validateStep2()
  }
}

// メディアノード: ファイル選択ボタンクリック
const handleMediaSelectClick = (index: number) => {
  // 非表示のfile inputをクリック
  const fileInput = document.getElementById(`fileInput-${index}`) as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

// メディアノード: ファイル選択時のハンドラー
const handleMediaFileChange = async (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック
  const imageTypes = ['image/png', 'image/jpeg']
  const videoTypes = ['video/mp4']

  const isImage = imageTypes.includes(file.type)
  const isVideo = videoTypes.includes(file.type)

  if (!isImage && !isVideo) {
    alert('対応形式はpng、jpg、mp4です。')
    input.value = '' // 入力をクリア
    return
  }

  if (isImage && !isSupportedImageExtension(file.name)) {
    alert('対応形式はpng、jpg、mp4です。')
    input.value = ''
    return
  }

  // ファイルサイズチェック（画像8MB未満、動画25MB未満）
  const maxSize = isImage ? 8 * 1024 * 1024 : 25 * 1024 * 1024
  const sizeLabel = isImage ? '8MB' : '25MB'
  const fileTypeLabel = isImage ? '画像' : '動画'

  if (file.size >= maxSize) {
    notifyError(`${fileTypeLabel}ファイルのサイズは${sizeLabel}未満にしてください。`)
    input.value = '' // 入力をクリア
    return
  }

  // 画像/動画ともにAPIアップロードを実行し、mediaId を保存する
  try {
    const result = await store.uploadMedia(file)
    store.updateMediaNode(index, {
      mediaId: result.mediaId,
      url: result.url,
      fileType: isImage ? 'image' : 'video',
      fileName: result.mediaOriginName || file.name
    })
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_media',
        screen: 'InstagramScenarioEditorDialog.StepMessage',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: 'message_media',
        fileType: isImage ? 'image' : 'video',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    alert('メディアのアップロードに失敗しました。')
  }

  // 入力をクリア（同じファイルを再選択可能にする）
  input.value = ''
}

// メディアノード: 削除ボタンクリック
const handleMediaDelete = (index: number) => {
  // blob URLを解放
  const message = messages.value[index]
  if (message && message.type === 'media' && message.url && message.url.startsWith('blob:')) {
    URL.revokeObjectURL(message.url)
  }

  // ストアから削除
  store.clearMediaNode(index)
  store.validateStep2()
}

// アンケートノード: 質問テキスト変更
const handleQuestionChange = (index: number, newQuestion: string) => {
  store.updateQuickReplyNode(index, newQuestion, undefined)
  // リアルタイムバリデーション
  store.validateStep2()
}

// アンケートノード: 選択肢のテキスト配列を取得
const getOptionTexts = (index: number): string[] => {
  const message = messages.value[index]
  if (message && message.type === 'select') {
    return message.options.map(opt => opt.text)
  }
  return []
}

// アンケートノード: 選択肢の配列を更新（Tagsコンポーネントから）
const handleOptionsChange = (index: number, newTexts: string[]) => {
  if (campaignRunning.value) {
    return
  }
  const message = messages.value[index]
  if (message && message.type === 'select') {
    // 13個の上限チェック
    if (newTexts.length > 13) {
      // 上限を超える場合は13個までに制限
      newTexts = newTexts.slice(0, 13)
    }

    const newOptions = newTexts.map(text => ({ text }))
    store.updateQuickReplyNode(index, undefined, newOptions)
    // リアルタイムバリデーション
    store.validateStep2()
  }
}

// ============================================
// カードノード用のハンドラー関数
// ============================================

// カードノード: 画像選択ボタンクリック
const handleCardMediaSelectClick = (index: number) => {
  const fileInput = document.getElementById(`cardFileInput-${index}`) as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

// カードノード: 画像ファイル変更
const handleCardMediaFileChange = async (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック（png/jpgのみ）
  const imageTypes = ['image/png', 'image/jpeg']
  if (!imageTypes.includes(file.type)) {
    notifyError('対応形式はpng、jpgです。')
    input.value = ''
    return
  }

  if (!isSupportedImageExtension(file.name)) {
    notifyError('対応形式はpng、jpgです。')
    input.value = ''
    return
  }

  // ファイルサイズチェック（8MB未満）
  const maxSize = 8 * 1024 * 1024
  if (file.size >= maxSize) {
    notifyError('画像ファイルのサイズは8MB未満にしてください。')
    input.value = ''
    return
  }

  // APIアップロードを実行
  try {
    const result = await store.uploadMedia(file)
    // アップロード成功時、mediaIdとurlとfileNameを保存
    store.updateCardNode(index, {
      mediaId: result.mediaId,
      url: result.url,
      fileName: result.mediaOriginName || file.name
    })
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_card_media',
        screen: 'InstagramScenarioEditorDialog.StepMessage',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: 'message_card',
        fileType: 'image',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    alert('画像のアップロードに失敗しました。')
  }

  // バリデーション
  store.validateStep2()

  // 入力をクリア（同じファイルを再度選択可能にする）
  input.value = ''
}

// カードノード: 画像削除
const handleCardMediaDelete = (index: number) => {
  store.clearCardMedia(index)
  store.validateStep2()
}

// カードノード: タイトル変更
const handleCardTitleChange = (index: number, newTitle: string) => {
  store.updateCardNode(index, { title: newTitle })
  store.validateStep2()
}

// カードノード: サブタイトル変更
const handleCardSubtitleChange = (index: number, newSubtitle: string) => {
  store.updateCardNode(index, { subtitle: newSubtitle })
  store.validateStep2()
}

// カードノード: デフォルトURL変更
const handleCardDefaultUrlChange = (index: number, newUrl: string) => {
  store.updateCardNode(index, { defaultUrl: newUrl })
}

// カードノード: ボタン追加
const handleCardButtonAdd = (index: number) => {
  if (campaignRunning.value) {
    return
  }
  store.addCardButton(index)
  store.validateStep2()
}

// カードノード: ボタン削除
const handleCardButtonDelete = (index: number, buttonIndex: number) => {
  if (campaignRunning.value) {
    return
  }
  store.removeCardButton(index, buttonIndex)
  store.validateStep2()
}

// カードノード: ボタンラベル変更
const handleCardButtonLabelChange = (index: number, buttonIndex: number, newLabel: string) => {
  const message = messages.value[index]
  if (message && message.type === 'card') {
    const newButtons = [...message.buttons]
    newButtons[buttonIndex] = { ...newButtons[buttonIndex], label: newLabel }
    store.updateCardNode(index, { buttons: newButtons })
    store.validateStep2()
  }
}

// カードノード: ボタンURL変更
const handleCardButtonUrlChange = (index: number, buttonIndex: number, newUrl: string) => {
  const message = messages.value[index]
  if (message && message.type === 'card') {
    const newButtons = [...message.buttons]
    newButtons[buttonIndex] = { ...newButtons[buttonIndex], url: newUrl }
    store.updateCardNode(index, { buttons: newButtons })
  }
}

// カードノード: ボタン追加可能かチェック
const canAddCardButton = (index: number): boolean => {
  const message = messages.value[index]
  if (!message || message.type !== 'card') {
    return false
  }
  return message.buttons.length < 3
}

// カードノード: エラー取得関数
const getCardMediaError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.media`] || null
}

const getCardTitleError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.title`] || null
}

const getCardSubtitleError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.subtitle`] || null
}
const getCardDefaultUrlError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) return null
  return step2Errors[`messages.${index}.defaultUrl`] || null
}

const getCardButtonLabelError = (index: number, buttonIndex: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.buttons.${buttonIndex}.label`] || null
}
const getCardButtonUrlError = (index: number, buttonIndex: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) return null
  return step2Errors[`messages.${index}.buttons.${buttonIndex}.url`] || null
}

const notifyError = (message: string) => {
  proxy.$notify({
    title: message,
    customClass: 'danger',
    duration: 5000
  })
}

/**
 * メッセージノードの変更を監視してリアルタイムバリデーション
 */
watch(
  messages,
  () => {
    store.validateStep2()
  },
  { deep: true }
)
</script>

<style scoped lang="scss">
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.step-message {
  .message-nodes-list {
    margin-bottom: $spacing-unit * 3;
  }

  .message-node-item {
    padding: $spacing-unit * 2.5;
    margin-bottom: $spacing-unit * 3;
    background-color: #fff;
    border: 1px solid $border-color;
    border-radius: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .message-node-type {
    margin-bottom: $spacing-unit * 2;
  }

  .message-node-content {
    margin-bottom: $spacing-unit * 2;

    // 共通フォームグループスタイル
    .form-group {
      margin-bottom: $spacing-unit * 2;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .form-label {
      display: block;
      margin-bottom: $spacing-unit;
      font-size: 14px;
      font-weight: 500;
      color: $text-color;

      .required-badge {
        display: inline-block;
        padding: 2px 8px;
        margin-left: $spacing-unit;
        font-size: 12px;
        font-weight: normal;
        vertical-align: middle;
        color: $text-color-white;
        background-color: $danger-color;
        border-radius: 12px;
      }
    }

    .input-invalid-text {
      margin-top: math.div($spacing-unit, 2);
      font-size: 14px;
      color: $danger-color;
    }

    .is-error {
      :deep(.el-input__inner),
      :deep(.el-textarea__inner) {
        border-color: $danger-color !important;
      }
    }

    .text-node-form {
      :deep(.el-textarea__inner) {
        font-size: 14px;
      }

      .is-error {
        :deep(.el-textarea__inner) {
          border-color: $danger-color !important;
        }
      }

      .input-invalid-text {
        margin-top: math.div($spacing-unit, 2);
        font-size: 14px;
        color: $danger-color;
      }
    }

    .node-placeholder {
      padding: $spacing-unit * 4;
      font-size: 14px;
      color: $sub-text-color;
      text-align: center;
      background-color: #fafafa;
      border: 1px dashed $border-color;
      border-radius: 4px;
    }

    .select-node-form {
      .field-label {
        display: block;
        margin-bottom: $spacing-unit;
        font-size: 14px;
        font-weight: 500;
        color: $text-color;
      }

      .question-field {
        margin-bottom: $spacing-unit * 2;

        :deep(.el-textarea__inner) {
          font-size: 14px;
        }

        .is-error {
          :deep(.el-textarea__inner) {
            border-color: $danger-color !important;
          }
        }

        .input-invalid-text {
          margin-top: math.div($spacing-unit, 2);
          font-size: 14px;
          color: $danger-color;
        }
      }

      .options-section {
        .options-label {
          margin-bottom: $spacing-unit * 1.5;

          .options-count-label {
            display: inline-flex;
            align-items: center;
            font-size: 14px;
            font-weight: 500;
            color: $text-color;
          }
        }

        .options-tags-wrapper {
          margin-bottom: $spacing-unit * 2;
        }

        .select-node-errors {
          margin-top: $spacing-unit;

          .input-invalid-text {
            margin-bottom: math.div($spacing-unit, 2);
            font-size: 14px;
            color: $danger-color;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }

    .media-node-form {
      .media-upload-container {
        display: flex;
        flex-direction: row;
        align-items: center;

        .upload-btn {
          margin-right: $spacing-unit * 2;

          .mdi {
            margin-right: math.div($spacing-unit, 2);
          }
        }

        .upload-hint {
          margin: 0;
          font-size: 12px;
          color: $sub-text-color;
        }
      }

      .media-preview {
        position: relative;
        display: inline-block;
        padding: math.div($spacing-unit, 2) $spacing-unit;
        border: 1px solid $border-color;
        border-radius: $round;

        .media-name {
          padding-right: $spacing-unit * 3;
          font-size: 14px;
          font-weight: 500;
          color: $text-color;
          word-break: break-all;
        }

        .mdi-close {
          position: absolute;
          top: math.div($spacing-unit, 2);
          right: math.div($spacing-unit, 2);
          font-size: 18px;
          color: $text-color;
          cursor: pointer;

          &:hover {
            color: $danger-color;
          }
        }

        .direct-media {
          display: block;
          max-width: 100%;
          max-height: $spacing-unit * 32;
        }
      }
    }
  }

  .message-node-footer {
    display: flex;
    justify-content: flex-start;
  }

  .message-delete-btn {
    .mdi {
      margin-right: math.div($spacing-unit, 2);
    }
  }

  // カードノード専用スタイル
  .card-node-form {
    // STEP3/STEP4 のメディアプレビューと統一スタイル
    .card-preview {
      position: relative;
      display: inline-block;
      width: auto; // inline-block に合わせる
      padding: math.div($spacing-unit, 2) $spacing-unit;
      border: 1px solid $border-color;
      border-radius: $round;

      .media-name {
        padding-right: $spacing-unit * 3;
        margin-bottom: $spacing-unit;
        font-size: 14px;
        font-weight: 500;
        color: $text-color;
        word-break: break-all;
      }

      .mdi-close {
        position: absolute;
        top: math.div($spacing-unit, 2);
        right: math.div($spacing-unit, 2);
        font-size: 18px;
        color: $text-color;
        cursor: pointer;

        &:hover {
          color: $danger-color;
        }
      }

      .direct-media {
        display: block;
        max-width: 100%;
        max-height: $spacing-unit * 32;
      }

      .media-error-inline {
        margin-top: $spacing-unit;
      }
    }

    .card-upload {
      display: flex;
      flex-direction: row;
      align-items: center;

      .upload-btn {
        margin-right: $spacing-unit * 2;

        .mdi {
          margin-right: math.div($spacing-unit, 2);
        }
      }

      .upload-hint {
        margin: 0;
        font-size: 12px;
        color: $sub-text-color;
      }

      .media-error-inline {
        margin-left: $spacing-unit * 2;
        font-size: 14px;
        color: $danger-color;
      }
    }

    .card-buttons-preview {
      margin-bottom: $spacing-unit * 2;
      text-align: left;

      :deep(.el-button) {
        display: inline-flex;
        max-width: 100%;
        margin-right: $spacing-unit;
        pointer-events: none;

        &:last-child {
          margin-right: 0;
        }
      }

      :deep(.el-button > span) {
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .card-button-item {
      padding: $spacing-unit * 2;
      margin-bottom: $spacing-unit * 2;
      border: 1px solid $border-color;
      border-radius: 4px;

      .card-button-header {
        margin-bottom: $spacing-unit * 2;
        font-size: 14px;
        font-weight: bold;
        color: $text-color;
      }

      .card-button-fields {
        .form-group {
          margin-bottom: $spacing-unit * 2;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .card-button-delete-btn {
      margin-top: $spacing-unit * 2;

      .mdi {
        margin-right: math.div($spacing-unit, 2);
      }
    }

    .card-button-add-btn {
      width: 100%;

      .mdi {
        margin-right: math.div($spacing-unit, 2);
      }
    }
  }

  .add-message-section {
    padding: $spacing-unit * 2 0;
    text-align: center;

    .add-message-tooltip {
      display: block;
      width: 100%;
    }

    .add-message-btn {
      width: 100%;

      .mdi {
        margin-right: math.div($spacing-unit, 2);
      }
    }

    .limit-message {
      margin-top: $spacing-unit;
      font-size: 12px;
      color: $sub-text-color;
    }
  }
}
</style>
